<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1100"
    height="88vh"
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="news-dialog-card">
      <v-card-title class="news-header">
        <div class="news-heading">
          <div class="news-header-icon"><v-icon size="24">mdi-newspaper-variant-multiple-outline</v-icon></div>
          <div>
            <div class="news-title">News &amp; Activities</div>
            <div class="news-subtitle">Each named connection has one destination and its own daily schedule.</div>
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          aria-label="Close News and Activities"
          @click="closeDialog"
        />
      </v-card-title>

      <v-progress-linear
        v-if="initialLoading || busy || previewLoading || savingEntry || deleting"
        indeterminate
        color="primary"
        height="3"
      />
      <v-divider v-else />

      <v-card-text class="news-body">
        <v-alert
          v-if="previewOnly"
          type="info"
          variant="tonal"
          class="news-preview-banner mb-4"
          role="status"
        >
          <strong>Design preview</strong> — nothing is saved or scheduled.
        </v-alert>

        <template v-if="!editor">
          <section aria-labelledby="news-connections-title">
            <div class="news-section-head">
              <div>
                <h2 id="news-connections-title" class="text-subtitle-1 font-weight-bold">Live data connections</h2>
                <p class="text-body-2 text-medium-emphasis">
                  Configure each connection independently, including its destination, mapping and Bangkok schedule.
                </p>
              </div>
              <div class="d-flex align-center ga-2">
              <v-btn variant="text" prepend-icon="mdi-history" @click="showLogs()">History log</v-btn>
              <v-btn
                v-if="permissions.add"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-plus"
                @click="selectConnection(0)"
              >
                Add connection
              </v-btn>
              </div>
            </div>

            <v-text-field v-model="connectionSearch" label="Search connections" placeholder="Name, API URL or destination"
              prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" clearable hide-details class="mt-4" />

            <v-alert v-if="!sources.length && !initialLoading" type="info" variant="tonal" class="mt-4">
              No API connections yet. Add a connection and its destination together.
            </v-alert>
            <p v-else-if="!filteredConnections.length && !initialLoading" class="text-body-2 mt-4" role="status">
              No matching connections. Try another search or clear the search field.
            </p>

            <article v-for="source in filteredConnections" :key="source.id" class="news-connection mt-4"
              :aria-labelledby="`news-connection-${source.id}`">
              <div class="news-connection-head">
                <div class="news-min-width">
                  <div class="d-flex flex-wrap align-center ga-2">
                    <h3 :id="`news-connection-${source.id}`" class="text-subtitle-1 font-weight-bold">{{ source.name }}</h3>
                    <v-chip v-if="source.isPending" color="warning" size="small" variant="tonal">Pending</v-chip>
                  </div>
                  <p class="news-url text-caption text-medium-emphasis">GET · {{ source.connection.apiUrl }}</p>
                  <p class="text-caption mt-1">{{ source.sourceId ? destinationLabel(source.destinationId) : 'Setup needed — edit to choose a destination.' }}</p>
                </div>
                <div class="news-icon-actions">
                  <v-btn v-if="permissions.edit" icon="mdi-connection" variant="text" size="small" :aria-label="`Test connection ${source.name}`" :disabled="deleteBusy" @click="quickTest(source.id)" />
                  <v-btn v-if="permissions.edit" icon="mdi-pencil-outline" variant="text" size="small" :aria-label="`Edit connection ${source.name}`" :disabled="deleteBusy" @click="selectConnection(source.id)" />
                  <v-btn v-if="permissions.delete" icon="mdi-trash-can-outline" color="error" variant="text" size="small" :aria-label="`Delete connection ${source.name}`" :disabled="deleteBusy" @click="requestDelete(source.id)" />
                  <v-btn icon="mdi-history" variant="text" size="small" :aria-label="`Logs for ${source.name}`" @click="showLogs(source.id)" />
                </div>
              </div>
              <div class="news-card-controls">
                <v-switch v-model="source.dailyEnabled" label="Daily sync" color="primary" hide-details density="compact" inset
                  :disabled="!permissions.edit || source.isPending || !source.sourceId || deleteBusy" :aria-label="`Daily sync for ${source.name}`" />
                <v-text-field v-model="source.dailyTime" type="time" label="Time (Bangkok)" variant="outlined" density="compact"
                  hide-details class="news-schedule-time" :disabled="!permissions.edit || source.isPending || !source.sourceId || !source.dailyEnabled || deleteBusy"
                  :aria-label="`Daily time for ${source.name}`" />
                <span class="text-caption text-medium-emphasis">{{ source.dailyEnabled ? 'Daily on' : 'Schedule off' }}</span>
                <v-spacer />
                <template v-if="permissions.edit">
                  <v-btn variant="outlined" color="primary" :loading="runningEntry === source.sourceId"
                    :disabled="source.isPending || !source.sourceId || deleteBusy" @click="runNow(source.id, source.sourceId)">Run now</v-btn>
                  <v-btn color="primary" :loading="scheduleSaving === source.sourceId"
                    :disabled="source.isPending || !source.sourceId || deleteBusy" @click="saveSchedule(source)">Save schedule</v-btn>
                </template>
              </div>
            </article>
          </section>

        </template>

        <template v-else>
          <v-btn
            variant="text"
            prepend-icon="mdi-arrow-left"
            class="mb-4"
            :disabled="busy || savingEntry"
            @click="backToList"
          >
            Back to connections
          </v-btn>

          <section v-if="editor === 'connection'" aria-labelledby="news-connection-editor-title">
            <div class="news-editor-heading">
              <v-avatar color="primary" variant="tonal" rounded><v-icon>mdi-api</v-icon></v-avatar>
              <div>
                <h2 id="news-connection-editor-title" class="text-h6">
                  {{ selectedConnection ? 'Edit API connection' : 'Add API connection' }}
                </h2>
                <p class="text-body-2 text-medium-emphasis">
                  {{ selectedConnection
                    ? 'Update this connection, mapping, chunk limit and schedule.'
                    : '1 Connection — enter the details, then test them before choosing a destination.' }}
                </p>
              </div>
            </div>

            <v-alert v-if="previewOnly || localDevelopment" type="info" variant="tonal" class="mb-5">
              {{ previewOnly ? 'Use sample details to review the complete setup.' : 'Customer connection details can be added when available.' }}
              <template #append>
                <v-btn variant="text" :disabled="busy" @click="useMock">
                  {{ previewOnly ? 'Use sample connection' : 'Use local mock' }}
                </v-btn>
              </template>
            </v-alert>

            <v-form ref="connectionForm" @submit.prevent="testConnection">
              <v-text-field
                v-model="connectionName"
                label="Connection name"
                variant="outlined"
                :rules="[required]"
                :disabled="busy || savingEntry"
              />
              <v-row>
                <v-col cols="12" sm="4">
                  <v-text-field label="Authentication" model-value="Client credentials" variant="outlined" readonly hide-details />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field label="Method" model-value="GET" variant="outlined" readonly hide-details />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="connection.apiUrl"
                    label="News API URL"
                    variant="outlined"
                    :rules="[required]"
                    :disabled="busy || savingEntry"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field label="Auth method" model-value="POST" variant="outlined" readonly hide-details />
                </v-col>
                <v-col cols="12" sm="9">
                  <v-text-field
                    v-model="connection.authUrl"
                    label="Auth URL"
                    variant="outlined"
                    :rules="[required]"
                    :disabled="busy || savingEntry"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="connection.clientId"
                    label="Client ID"
                    variant="outlined"
                    :rules="[required]"
                    :disabled="busy || savingEntry"
                    autocomplete="off"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="connection.clientSecret"
                    :label="selectedConnection ? 'Client secret (leave blank to keep saved)' : 'Client secret'"
                    type="password"
                    variant="outlined"
                    :rules="[secretRule]"
                    :hint="selectedConnection && hasSecret ? 'A secret is saved. Leave this blank to retain it.' : ''"
                    :persistent-hint="selectedConnection && hasSecret"
                    :disabled="busy || savingEntry"
                    autocomplete="new-password"
                  />
                </v-col>
              </v-row>

              <div class="news-form-actions">
                <p class="text-body-2 text-medium-emphasis">
                  {{ selectedConnection && !connection.clientSecret
                    ? 'Test with the saved secret or a replacement. Results are recorded without saving settings or importing news.'
                    : 'Tests record results in History without saving settings or importing news.' }}
                </p>
                <div class="d-flex flex-wrap ga-3">
                  <v-btn
                    type="submit"
                    variant="outlined"
                    color="primary"
                    prepend-icon="mdi-connection"
                    :loading="busy"
                    :disabled="busy || savingEntry"
                  >
                    Test connection
                  </v-btn>
                </div>
              </div>
            </v-form>

          </section>

          <section
            v-if="showReturnedFields"
            class="news-returned-fields mt-5"
            aria-labelledby="news-returned-fields-title"
          >
            <h3 id="news-returned-fields-title" class="text-subtitle-1 font-weight-bold">
              Returned fields
            </h3>
            <p class="text-body-2 text-medium-emphasis mt-1 mb-4">
              Map returned news fields to the content used in each chunk.
            </p>
            <v-alert
              v-if="preview.records.length"
              type="success"
              variant="tonal"
              density="compact"
              role="status"
              class="mb-4"
            >
              <template v-if="previewOnly">
                Example response · {{ preview.records.length }} news
                {{ preview.records.length === 1 ? 'object' : 'objects' }} available for this preview.
              </template>
              <template v-else>
                Connection successful · {{ preview.records.length }} news
                {{ preview.records.length === 1 ? 'article' : 'articles' }} returned. Nothing has been imported.
              </template>
            </v-alert>
            <v-alert v-else type="info" variant="tonal" density="compact" role="status" class="mb-4">
              {{ emptyPreviewMessage }}
            </v-alert>

            <div class="news-identity-grid">
              <v-text-field
                :model-value="identityField"
                label="Identity field"
                variant="outlined"
                density="compact"
                readonly
                hide-details
              />
              <v-text-field
                :model-value="identityJsonPath"
                label="Identity JSON path"
                variant="outlined"
                density="compact"
                readonly
                hide-details
              />
            </div>
            <p class="text-caption text-medium-emphasis mt-3 mb-4">
              JSON paths are illustrative; actual paths follow the customer response.
            </p>

            <v-expansion-panels class="news-raw-json mt-3 mb-3" variant="accordion">
              <v-expansion-panel title="View sample JSON response">
                <v-expansion-panel-text><pre class="news-json">{{ previewJson }}</pre></v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <div class="news-chunk-preview-card mt-3" aria-label="Chunk review">
              <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-3">
                <h4 class="text-body-2 font-weight-bold">Chunk review</h4>
                <v-btn-toggle v-model="chunkView" mandatory color="primary" variant="outlined" density="compact" aria-label="Chunk review mode">
                  <v-btn value="edit">Edit template</v-btn>
                  <v-btn value="preview">Preview result</v-btn>
                </v-btn-toggle>
              </div>
                <p class="text-caption text-medium-emphasis mb-3">Edit the whole text, including its labels. Insert fields with <code v-pre>{{ field_name }}</code>; no JSON is required.</p>
                <div class="d-flex flex-wrap ga-2 mb-3" aria-label="Insert a returned field into Chunk template">
                  <v-btn v-for="field in templateFields" :key="field" size="small" variant="tonal" color="primary"
                    :disabled="savingEntry" :aria-label="`Insert ${field} into Chunk template`"
                    @click="insertTemplateField(field)">{{ field }}</v-btn>
                  <v-btn v-if="chunkView === 'edit'" variant="text" size="small" :disabled="savingEntry" @click="clearChunkTemplate">Clear template</v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mb-3">Supported fields: text, true/false, null and safe whole numbers. Fractions, unsafe numbers, files and objects are not supported.</p>
              <template v-if="chunkView === 'edit'">
                <label for="news-chunk-template" class="d-block text-body-2 mb-2">Chunk template</label>
                <div class="news-chunk-editor">
                <v-textarea id="news-chunk-template" ref="chunkTemplateInput" v-model="settings.chunkTemplate" aria-label="Chunk template"
                  class="news-editable-chunk" :rows="9" auto-grow variant="plain" density="compact" hide-details="auto"
                  :disabled="savingEntry" maxlength="100000"
                  @update:model-value="markTemplateEdited"
                  @focusin="captureTemplateSelection" @click="captureTemplateSelection"
                  @keyup="captureTemplateSelection" @select="captureTemplateSelection"
                  @input="captureTemplateSelection" @focusout="captureTemplateSelection" />
                </div>
              </template>
              <template v-else>
                <pre v-if="previewRecord && !templateError" class="news-chunk-text" aria-live="polite">{{ chunkTextPreview }}</pre>
                <p v-else-if="!previewRecord" class="text-body-2 text-medium-emphasis mt-3">{{ emptyPreviewMessage }}</p>
                <p v-else class="text-body-2 text-medium-emphasis mt-3">Fix the template error to see the preview.</p>
              </template>
              <v-alert v-if="templateError" type="error" variant="tonal" class="mt-3" role="alert">{{ templateError }}</v-alert>
              <v-alert v-if="legacyConversionRequired" type="warning" variant="tonal" class="mt-3">
                The old format flattened topic and published line breaks. The editable format preserves them and may change the chunk layout. Review Preview result before saving. Until you save, the old settings remain unchanged.
                <v-checkbox v-model="legacyConversionAccepted" label="Use editable format; preserve line breaks"
                  aria-label="Use editable format; preserve line breaks" :disabled="savingEntry" hide-details />
              </v-alert>
              <p class="text-caption text-medium-emphasis mt-3 mb-0">
                All text, including title and source lines, is editable. Long content is split automatically.
              </p>
            </div>
          </section>

          <section v-if="showReturnedFields" aria-label="Destination and chunk limit">
            <div class="news-editor-section">
              <h3 class="text-subtitle-1 font-weight-bold">
                Destination and chunk limit
              </h3>
              <p class="text-body-2 text-medium-emphasis mb-4">
                The destination is fixed after saving. Add a new connection for a different destination.
              </p>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="category"
                    :items="categories"
                    label="Category"
                    variant="outlined"
                    :disabled="!!selectedSource"
                    @update:model-value="itemId=''; settings.destinationId=null"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="itemId"
                    :items="items"
                    item-title="label"
                    item-value="id"
                    label="MetaDoc item"
                    variant="outlined"
                    :disabled="!!selectedSource || !category"
                    @update:model-value="settings.destinationId=null"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="settings.destinationId"
                    :items="versions"
                    item-title="label"
                    item-value="id"
                    label="Knowledge / Document version"
                    variant="outlined"
                    :disabled="!!selectedSource || !itemId"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="settings.maximumChunks"
                    label="Maximum total chunks"
                    type="number"
                    min="1"
                    max="100000"
                    variant="outlined"
                    :rules="[maximumChunksRule]"
                    hint="Counts all chunks already in this destination, including unrelated content."
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="news-cap-status">
                    <template v-if="destination">
                      <strong>{{ destination.chunks }} chunks already here</strong>
                      <span>{{ Math.max(0, (settings.maximumChunks || 0) - destination.chunks) }} available under this cap</span>
                    </template>
                    <span v-else>Select a document version to see its current chunk count.</span>
                  </div>
                </v-col>
              </v-row>
              <v-alert type="info" variant="tonal">
                The cap and current chunk count are destination-wide. Other connections targeting this destination share them.
                Existing unrelated chunks remain; a complete article that will not fit is skipped and logged.
              </v-alert>
            </div>

          </section>
        </template>

        <v-alert v-if="error" type="error" variant="tonal" role="alert" class="mt-4" aria-live="assertive">
          {{ error }}
        </v-alert>
        <v-alert v-if="notice" type="success" variant="tonal" role="status" class="mt-4" aria-live="polite" closable @click:close="dismissNotice">
          {{ notice }}
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-5 py-4">
        <span class="text-caption text-medium-emphasis">
          {{ previewOnly
            ? 'Preview actions are simulated and stay in this dialog.'
            : 'Tests and saves do not import. Use Run now or enable a schedule when you are ready.' }}
        </span>
        <v-spacer />
        <v-btn variant="text" @click="closeDialog">Close</v-btn>
        <v-btn v-if="editor === 'connection' && (selectedConnection ? permissions.edit : permissions.add)" color="primary" variant="flat" prepend-icon="mdi-content-save"
          :loading="savingEntry" :disabled="deleteBusy || (connectionTested && !canSaveSetup)"
          @click="connectionTested ? saveSetup() : savePending()">
          {{ connectionTested ? 'Save connection' : 'Save as pending' }}
        </v-btn>
      </v-card-actions>
  <v-dialog :model-value="historyOpen" max-width="1000" height="82vh" scrollable @update:model-value="value => { if (!value) hideLogs(); }">
    <v-card>
      <v-card-title class="d-flex flex-wrap align-center ga-2">
        <v-btn v-if="viewedLog" variant="text" prepend-icon="mdi-arrow-left" @click="backToHistory">Back to history</v-btn>
        <span v-if="!viewedLog">History log</span>
        <v-spacer /><v-btn variant="text" :loading="loadingLogs || logResponseLoading" @click="viewedLog ? viewLogResponse(viewedLog) : refreshLogs()">Refresh</v-btn>
        <div v-if="viewedLog" class="w-100 mt-2">History details</div>
      </v-card-title>
      <v-card-text v-if="!viewedLog">
        <p class="text-caption mb-4">Tests and imports · newest first · all times Bangkok (UTC+7). Tests never save connection settings or import news.</p>
        <v-row>
          <v-col cols="12" sm="8"><v-select v-model="historyConnection" :items="historyConnectionOptions" label="Connection" variant="outlined" @update:model-value="changeHistoryFilter" /></v-col>
          <v-col cols="12" sm="4"><v-select v-model="historyStatus" :items="['All', 'Complete', 'Failed']" label="Status" variant="outlined" @update:model-value="changeHistoryFilter" /></v-col>
        </v-row>
        <v-alert v-if="historyError" type="error" variant="tonal" role="alert">{{ historyError }}</v-alert>
        <p v-if="loadingLogs" role="status">Loading history…</p>
        <v-table v-else>
          <thead><tr><th>Time (Bangkok)</th><th>Connection</th><th>Operation</th><th>Status</th><th>Details</th></tr></thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id">
              <td>{{ logTime(log.createdAt) }}</td><td>{{ log.connectionName || 'Connection unavailable' }}<span v-if="log.unsaved" class="d-block text-caption">Unsaved test</span></td>
              <td>{{ log.operation || 'Sync run' }}</td><td><v-chip :color="logStatusColor(log.status)" variant="tonal" size="small">{{ log.status || 'Unknown' }}</v-chip></td>
              <td><v-btn variant="text" size="small" @click="viewLogResponse(log)">View details</v-btn></td>
            </tr>
            <tr v-if="!logs.length && !historyError"><td colspan="5">No history matches these filters.</td></tr>
          </tbody>
        </v-table>
        <div class="d-flex flex-wrap align-center ga-2 mt-4">
          <span class="text-caption">{{ historyTotal }} records · Page {{ historyPage }} of {{ historyPages }}</span><v-spacer />
          <v-btn variant="text" :disabled="loadingLogs || historyPage <= 1" @click="changeHistoryPage(historyPage - 1)">Previous</v-btn>
          <v-btn variant="text" :disabled="loadingLogs || historyPage >= historyPages" @click="changeHistoryPage(historyPage + 1)">Next</v-btn>
        </div>
      </v-card-text>
      <v-card-text v-else>
        <p class="mb-2">{{ viewedLog.connectionName || 'Connection unavailable' }} · {{ viewedLog.operation || 'Sync run' }}</p>
        <p v-if="viewedLog.endpoint" class="news-url text-caption mb-2">{{ viewedLog.endpoint }}</p>
        <p class="text-caption mb-3">{{ logTime(viewedLog.createdAt) }} (Bangkok)</p>
        <p v-if="previewOnly" class="mb-3">Design preview — simulated response.</p>
        <p v-if="logResponseLoading" role="status">Loading details…</p>
        <v-alert v-else-if="logResponseError" type="error" variant="tonal">{{ logResponseError }}</v-alert>
        <template v-else>
          <v-alert :type="viewedLog.status === 'Failed' ? 'error' : viewedLog.status === 'Retrying' ? 'warning' : viewedLog.status === 'Complete' ? 'success' : 'info'" variant="tonal" class="mb-3" role="status">{{ logResponseMessage }}</v-alert>
          <p v-if="viewedLog.attemptCount != null" class="mb-2">Attempts: {{ viewedLog.attemptCount }}<template v-if="viewedLog.maxAttempts != null"> of {{ viewedLog.maxAttempts }}</template></p>
          <p v-if="viewedLog.nextAttemptAt" class="mb-2">Next retry: {{ logTime(viewedLog.nextAttemptAt) }} (Bangkok)</p>
          <p v-if="viewedLog.legacy" class="text-body-2 mb-3">Some details were not recorded for this older run.</p>
          <v-expansion-panels v-model="responsePanel" variant="accordion" multiple>
            <v-expansion-panel v-if="viewedLog.errorDetails" title="Error details"><v-expansion-panel-text><pre class="news-response-json">{{ diagnosticText(viewedLog.errorDetails) }}</pre></v-expansion-panel-text></v-expansion-panel>
            <v-expansion-panel v-if="logResponseText" title="Response JSON (recorded, sanitized)">
              <v-expansion-panel-text><pre class="news-response-json">{{ logResponseText }}</pre></v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel v-if="viewedLog.responseText" title="Response body (plain text)"><v-expansion-panel-text><pre class="news-response-json">{{ viewedLog.responseText }}</pre></v-expansion-panel-text></v-expansion-panel>
            <v-expansion-panel v-for="(attempt, index) in diagnosticAttempts" :key="`diagnostic-${attempt.id}-${index}`" :title="`Attempt details · ${logTime(attempt.createdAt)}`">
              <v-expansion-panel-text>
                <p v-if="attempt.endpoint" class="news-url">{{ attempt.endpoint }}</p>
                <pre v-if="attempt.errorDetails" class="news-response-json">{{ diagnosticText(attempt.errorDetails) }}</pre>
                <pre v-if="attempt.responseJson" class="news-response-json">{{ diagnosticText(attempt.responseJson) }}</pre>
                <pre v-if="attempt.responseText" class="news-response-json">{{ attempt.responseText }}</pre>
                <p v-if="attempt.responseNote">{{ attempt.responseNote }}</p>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <p v-if="viewedLog.responseNote" class="mt-3">{{ viewedLog.responseNote }}</p>
          <p v-else-if="!logResponseText && !viewedLog.responseText" class="mt-3">No response body was recorded.</p>
        </template>
      </v-card-text>
      <v-card-actions><v-spacer /><v-btn @click="hideLogs">Close history</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog :model-value="!!pendingDelete" max-width="520" persistent>
    <v-card v-if="pendingDelete">
      <v-card-title>Delete connection?</v-card-title>
      <v-card-text>
        <p>Remove “{{ pendingDelete.name }}” and disable its schedule?</p>
        <p class="mt-3">Existing chunks and history will be kept. The destination document itself will not be deleted.</p>
        <v-alert v-if="error" class="mt-3" type="error" variant="tonal" role="alert">{{ error }}</v-alert>
        <v-alert class="mt-3" type="info" variant="tonal">
          {{ previewOnly ? 'Design preview: only sample cards will be removed. No saved data will change.' : 'This removes the connection configuration and disables future runs.' }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn autofocus :disabled="deleting" @click="cancelDelete">Cancel</v-btn>
        <v-btn color="error" variant="flat" :disabled="deleteBusy" @click="confirmDelete">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { newChunkTemplate, editableChunkTemplate, renderTemplate, validateTemplate, scalarFields } from '../utils/newsTextTemplate.mjs';

const blankConnection = () => ({ apiUrl: '', authUrl: '', clientId: '', clientSecret: '' });
const blankEntry = () => ({
  destinationId: null,
  topicField: 'th_content_title',
  contentField: 'th_detail',
  chunkTemplate: newChunkTemplate(),
  chunkTemplateIncludesHeader: true,
  publishedField: 'publish_at',
  maximumChunks: null,
  dailyEnabled: false,
  dailyTime: '08:00',
});
const designPreviewDestinations = () => [
  { id: 9001, category: 'Others', itemId: 'OTHER0001', itemName: 'News & Activities', version: 'V001', name: 'Morning News', title: '__OTHERS__', subTitle: '', chunks: 24 },
  { id: 9003, category: 'Others', itemId: 'OTHER0001', itemName: 'News & Activities', version: 'V003', name: 'Shield', title: '__OTHERS__', subTitle: '', chunks: 38 },
  { id: 9011, category: 'Others', itemId: 'OTHER0002', itemName: 'Community News', version: 'V001', name: 'Community Update', title: '__OTHERS__', subTitle: '', chunks: 12 },
  { id: 9101, category: 'Product', itemId: 'PRODUCT0001', itemName: 'Product Highlights', version: 'V001', name: 'Featured Product', title: 'Featured Product', subTitle: 'Product Highlights', chunks: 0 },
];
const designPreviewArticles = () => ({
  records: [
    {
      content_id: 1001,
      th_content_title: 'ข่าวประชาสัมพันธ์ตัวอย่าง',
      en_content_title: 'Example News',
      th_detail: 'รายละเอียดข่าวภาษาไทย',
      en_detail: 'English news detail',
      category: 'NE',
      banner_pic: 'https://cdn.example.com/content/1001/banner.jpg',
      status: 'PUBLISH',
      updated_by: 'local-mock',
      created_at: '2026-08-24T00:00:00+07:00',
      updated_at: '2026-08-24T00:00:00+07:00',
      publish_at: '2026-08-24T08:00:00+07:00',
      notification: true,
      is_announce: true,
      version: '1.0',
      video_url_en: 'https://video.example.com/en/1001',
      video_url_th: 'https://video.example.com/th/1001',
      is_schedule: true,
      effective_date: '2026-08-24T00:00:00+07:00',
      expiry_date: '2026-08-24T23:59:59+07:00',
    },
    {
      content_id: 1002,
      th_content_title: 'กิจกรรมตัวอย่าง',
      en_content_title: 'Example Activity',
      th_detail: 'รายละเอียดกิจกรรมภาษาไทย\n\nข้อมูลเพิ่มเติมเกี่ยวกับกิจกรรม',
      en_detail: 'English activity detail',
      category: 'PR',
      banner_pic: 'https://cdn.example.com/content/1002/banner.jpg',
      status: 'PUBLISH',
      updated_by: 'local-mock',
      created_at: '2026-08-24T00:00:00+07:00',
      updated_at: '2026-08-24T00:00:00+07:00',
      publish_at: '2026-08-24T08:00:00+07:00',
      notification: true,
      is_announce: false,
      version: '1.0',
      video_url_en: 'https://video.example.com/en/1002',
      video_url_th: 'https://video.example.com/th/1002',
      is_schedule: true,
      effective_date: '2026-08-24T00:00:00+07:00',
      expiry_date: '2026-08-24T23:59:59+07:00',
    },
  ],
});

export default {
  name: 'NewsApiConnectionDialog',
  props: {
    modelValue: Boolean,
    previewOnly: { type: Boolean, default: false },
    // สิทธิ์ของหน้าเจ้าของ dialog (MetaDoc ส่ง $pagePermission ของ 'MetaDoc') — ดูรายการ/ประวัติได้เสมอ
    // add = เพิ่ม connection · edit = แก้/ทดสอบ/ตั้งเวลา/Run now · delete = ลบ connection
    permissions: { type: Object, default: () => ({ add: true, edit: true, delete: true }) },
  },
  emits: ['update:modelValue'],
  data: () => ({
    pendingDelete: null,
    deleting: false,
    sources: [],
    connectionSearch: '',
    savedSchedules: {},
    chunkView: 'edit',
    newTemplateDraft: true,
    generatedHeaderLines: ['title: ', 'sub-title: '],
    templateHydrationError: '',
    legacyConversionRequired: false,
    legacyConversionAccepted: false,
    templateSelection: null,
    destinations: [],
    initialLoading: false,
    editor: '',
    selectedConnection: 0,
    selectedSource: 0,
    connectionName: '',
    connection: blankConnection(),
    hasSecret: false,
    settings: blankEntry(),
    category: '',
    itemId: '',
    preview: null,
    testedConnectionFingerprint: '',
    previewLoading: false,
    busy: false,
    savingEntry: false,
    scheduleSaving: null,
    runningEntry: null,
    loadingLogs: false,
    historyOpen: false,
    historyConnection: 'all',
    historyStatus: 'All',
    historyPage: 1,
    historyTotal: 0,
    historyConnections: [],
    historyError: '',
    logs: [],
    viewedLog: null,
    logResponseLoading: false,
    logResponseText: '',
    logResponseMessage: '',
    logResponseError: '',
    logAttempts: [],
    responsePanel: undefined,
    logResponseSequence: 0,
    previewLogs: [],
    previewNextConnectionId: 10001,
    previewNextEntryId: 20001,
    previewNextLogId: 30001,
    error: '',
    notice: '',
    noticeTimer: null,
    requestSequence: 0,
    logRequestSequence: 0,
    logTimer: null,
  }),
  computed: {
    filteredConnections() {
      const query = (this.connectionSearch || '').trim().toLowerCase();
      if (!query) return this.sources;
      return this.sources.filter(source => [
        source.name,
        source.connection?.apiUrl,
        source.sourceId ? this.destinationLabel(source.destinationId) : '',
      ].some(value => String(value || '').toLowerCase().includes(query)));
    },
    historyPages() { return Math.max(1, Math.ceil(this.historyTotal / 20)); },
    historyConnectionOptions() {
      return [{ title: 'All connections', value: 'all' }, { title: 'Unsaved tests', value: 'unsaved' },
        ...this.historyConnections.map(x => ({ title: x.name, value: x.id }))];
    },
    diagnosticAttempts() {
      return this.logAttempts.filter(x => x.errorDetails || x.responseJson || x.responseText || x.responseNote);
    },
    deleteBusy() {
      return !!(this.deleting || this.busy || this.initialLoading || this.savingEntry || this.scheduleSaving || this.runningEntry || this.previewLoading);
    },
    localDevelopment() {
      return import.meta.env.DEV && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
    },
    currentParent() {
      return this.sources.find(x => x.id === this.selectedConnection);
    },
    selectedParentName() {
      return this.currentParent?.name || 'Connection unavailable';
    },
    categories() {
      return [...new Set(this.destinations.map(x => this.categoryLabel(x.category)))];
    },
    items() {
      return [...new Map(
        this.destinations
          .filter(x => this.categoryLabel(x.category) === this.category)
          .map(x => [x.itemId, { id: x.itemId, label: `${x.itemId} — ${x.itemName || ''}` }]),
      ).values()];
    },
    versions() {
      return this.destinations
        .filter(x => this.categoryLabel(x.category) === this.category && x.itemId === this.itemId)
        .map(x => ({ ...x, label: `${x.version} — ${x.name}` }));
    },
    destination() {
      return this.destinations.find(x => x.id === this.settings.destinationId);
    },
    connectionFingerprint() {
      return JSON.stringify([
        this.connection.apiUrl?.trim(),
        this.connection.authUrl?.trim(),
        this.connection.clientId?.trim(),
        this.connection.clientSecret || '',
      ]);
    },
    connectionTested() {
      return !!this.preview && this.testedConnectionFingerprint === this.connectionFingerprint;
    },
    showReturnedFields() {
      return this.connectionTested;
    },
    identityField() {
      return 'content_id';
    },
    identityJsonPath() {
      return '$.data[*].content_id';
    },
    templateFields() { return scalarFields(this.previewRecord); },
    templateError() {
      if (this.templateHydrationError) return this.templateHydrationError;
      try {
        validateTemplate(this.settings.chunkTemplate);
        for (const record of this.preview?.records || []) {
          if (!renderTemplate(this.settings.chunkTemplate, record).trim()) throw new Error('Rendered text must not be blank.');
        }
      } catch (error) { return `Chunk template: ${error.message}`; }
      return '';
    },
    previewRecord() {
      return this.preview?.records?.[0] || null;
    },
    previewJson() {
      return JSON.stringify((this.preview?.records || []).slice(0, 20), null, 2);
    },
    emptyPreviewMessage() {
      return 'No articles were returned for this day. You can still review or save the field mapping.';
    },
    chunkTextPreview() {
      if (!this.previewRecord || this.templateError) return '';
      return renderTemplate(this.settings.chunkTemplate, this.previewRecord);
    },
    fieldOptions() {
      return [...new Set([
        this.settings.topicField,
        this.settings.contentField,
        this.settings.publishedField,
        'th_content_title',
        'th_detail',
        'publish_at',
        ...(this.preview?.records || []).flatMap(record => (
          Object.keys(record).filter(key => typeof record[key] === 'string' || record[key] === null)
        )),
      ].filter(Boolean))];
    },
    entryFieldsValid() {
      const maximumChunks = Number(this.settings.maximumChunks);
      return !!(
        this.destination
        && !this.templateError
        && Number.isInteger(maximumChunks)
        && maximumChunks > 0
        && maximumChunks >= this.destination.chunks
        && maximumChunks <= 100000
      );
    },
    canSaveSetup() {
      return !!((!this.legacyConversionRequired || this.legacyConversionAccepted)
        && this.connectionTested && this.connectionName.trim()
        && this.connection.apiUrl?.trim() && this.connection.authUrl?.trim() && this.connection.clientId?.trim()
        && (this.hasSecret || this.connection.clientSecret?.trim()) && this.entryFieldsValid);
    },
  },
  watch: {
    notice(value) {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = value ? setTimeout(() => this.dismissNotice(), 5000) : null;
    },
    'settings.destinationId'() { this.prefillNewTemplate(); },
    modelValue(value) {
      this.pendingDelete = null;
      if (!value) {
        this.hideLogs();
        this.viewedLog = null;
        this.dismissNotice();
        this.requestSequence += 1;
        this.logRequestSequence += 1;
        this.loadingLogs = false;
        this.busy = false;
        this.preview = null;
        this.testedConnectionFingerprint = '';
        this.connection.clientSecret = '';
      }
      if (!this.previewOnly) return;
      if (value) this.initializePreviewState();
      else this.resetPreviewState();
    },
    connection: {
      deep: true,
      handler() {
        if (this.editor === 'connection') {
          this.preview = null;
          this.testedConnectionFingerprint = '';
          this.error = '';
        }
      },
    },
  },
  mounted() {
    if (this.previewOnly) {
      this.initializePreviewState();
      return;
    }
    this.initialLoading = true;
    this.load().catch(this.fail).finally(() => { this.initialLoading = false; });
  },
  beforeUnmount() {
    this.hideLogs();
    this.viewedLog = null;
    this.dismissNotice();
    this.requestSequence += 1;
    this.logRequestSequence += 1;
    this.loadingLogs = false;
    this.connection.clientSecret = '';
    this.logs = [];
    clearTimeout(this.logTimer);
    if (this.previewOnly) this.resetPreviewState();
  },
  methods: {
    categoryLabel(value) {
      return value === '__OTHERS__' ? 'Others' : value;
    },
    dismissNotice() {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
      this.notice = '';
    },
    requestDelete(connectionId) {
      if (this.deleteBusy) return;
      const target = this.sources.find(x => x.id === connectionId);
      if (target) this.pendingDelete = { connectionId, name: target.name };
    },
    cancelDelete() { if (!this.deleting) this.pendingDelete = null; },
    async confirmDelete() {
      if (!this.pendingDelete || this.deleteBusy) return;
      const { connectionId } = this.pendingDelete;
      this.deleting = true;
      this.clearFeedback();
      try {
        if (!this.previewOnly) {
          const result = await this.api('DeleteConnection', { id: connectionId });
          if (!result?.deleted) throw new Error('Deletion was not confirmed. Reload and try again.');
        }
        this.sources = this.sources.filter(x => x.id !== connectionId);
        this.backToList();
        this.selectedConnection = 0;
        this.selectedSource = 0;
        this.notice = `${this.previewOnly ? 'Design preview: ' : ''}Connection removed. Existing chunks and history were kept.`;
      } catch (error) { this.fail(error); }
      finally { this.deleting = false; }
    },
    async api(action, request = {}) {
      if (this.previewOnly) throw new Error('Design preview cannot call the News API.');
      const result = await this.$call.NewsApiRequest(action, request);
      if (result.status !== 0) throw new Error(result.message || 'Operation failed.');
      return result.data;
    },
    fail(error) {
      this.error = error?.response?.data?.message || error?.message || 'Operation failed. Try again.';
    },
    async load(savedConnectionId = 0) {
      if (this.previewOnly) {
        this.initializePreviewState();
        return;
      }
      const [sources, destinations] = await Promise.all([
        this.api('Connections'),
        this.api('Destinations'),
      ]);
      const drafts = new Map(this.sources.map(source => [source.id, source]));
      const schedules = {};
      this.sources = (sources || []).map(source => {
        const draft = drafts.get(source.id);
        const baseline = this.savedSchedules[source.sourceId];
        const dirty = !source.isPending && source.id !== savedConnectionId && draft && baseline
          && (draft.dailyEnabled !== baseline.dailyEnabled || draft.dailyTime !== baseline.dailyTime);
        schedules[source.sourceId] = { dailyEnabled: source.dailyEnabled, dailyTime: source.dailyTime };
        return {
          ...source, connection: { ...source.connection, clientSecret: '' },
          ...(dirty ? { dailyEnabled: draft.dailyEnabled, dailyTime: draft.dailyTime } : {}),
        };
      });
      this.savedSchedules = schedules;
      this.destinations = destinations || [];
    },
    initializePreviewState() {
      this.resetPreviewState();
      this.destinations = designPreviewDestinations();
    },
    resetPreviewState() {
      this.viewedLog = null;
      this.dismissNotice();
      this.pendingDelete = null;
      this.requestSequence += 1;
      this.logRequestSequence += 1;
      this.sources = [];
      this.savedSchedules = {};
      this.destinations = [];
      this.editor = '';
      this.selectedConnection = 0;
      this.selectedSource = 0;
      this.connectionName = '';
      this.connection = blankConnection();
      this.hasSecret = false;
      this.settings = blankEntry();
      this.category = '';
      this.itemId = '';
      this.preview = null;
      this.testedConnectionFingerprint = '';
      this.previewLoading = false;
      this.busy = false;
      this.savingEntry = false;
      this.scheduleSaving = null;
      this.runningEntry = null;
      this.loadingLogs = false;
      this.logs = [];
      this.previewLogs = [];
      this.previewNextConnectionId = 10001;
      this.previewNextEntryId = 20001;
      this.previewNextLogId = 30001;
      this.error = '';
      this.notice = '';
      clearTimeout(this.logTimer);
    },
    closeDialog() {
      this.hideLogs();
      this.viewedLog = null;
      this.dismissNotice();
      this.pendingDelete = null;
      if (this.previewOnly) this.resetPreviewState();
      this.$emit('update:modelValue', false);
    },
    clearFeedback() {
      this.error = '';
      this.dismissNotice();
    },
    backToList() {
      this.pendingDelete = null;
      this.logRequestSequence += 1;
      this.loadingLogs = false;
      this.editor = '';
      this.preview = null;
      this.testedConnectionFingerprint = '';
      this.previewLoading = false;
      this.logs = [];
      this.requestSequence += 1;
      this.clearFeedback();
    },
    selectConnection(id) {
      this.viewedLog = null;
      this.chunkView = 'edit';
      this.newTemplateDraft = false;
      this.generatedHeaderLines = ['title: ', 'sub-title: '];
      this.templateHydrationError = '';
      this.legacyConversionRequired = false;
      this.legacyConversionAccepted = false;
      this.templateSelection = null;
      this.requestSequence += 1;
      this.busy = false;
      this.pendingDelete = null;
      clearTimeout(this.logTimer);
      this.logRequestSequence += 1;
      this.loadingLogs = false;
      this.clearFeedback();
      this.preview = null;
      this.testedConnectionFingerprint = '';
      this.logs = [];
      this.editor = 'connection';
      this.selectedConnection = id;
      this.selectedSource = 0;
      this.settings = blankEntry();
      this.category = '';
      this.itemId = '';
      const source = this.sources.find(x => x.id === id);
      if (!source) {
        this.newTemplateDraft = true;
        this.connectionName = '';
        this.connection = blankConnection();
        this.hasSecret = false;
        return;
      }
      this.connectionName = source.name;
      this.connection = { ...source.connection, clientSecret: '' };
      this.hasSecret = !!source.hasSecret;
      this.selectedSource = source.sourceId || 0;
      this.settings = { ...blankEntry(), ...source };
      this.newTemplateDraft = !source.sourceId && source.chunkTemplateIncludesHeader !== true;
      this.legacyConversionRequired = !this.newTemplateDraft && source.chunkTemplateIncludesHeader !== true && source.chunkTemplate == null;
      try {
        this.settings.chunkTemplate = this.newTemplateDraft ? newChunkTemplate() : editableChunkTemplate(source, this.destination);
      }
      catch (error) {
        this.settings.chunkTemplate = source.chunkTemplate ?? '';
        this.templateHydrationError = error.message;
      }
      const target = this.destination;
      this.category = this.categoryLabel(target?.category) || '';
      this.itemId = target?.itemId || '';
    },
    markTemplateEdited() {
      if (this.newTemplateDraft) {
        this.reconcileGeneratedHeaders();
        this.templateHydrationError = '';
      }
    },
    reconcileGeneratedHeaders() {
      const lines = this.settings.chunkTemplate.split('\n');
      // Only the two original generated lines belong to this draft helper.
      // Once changed, moved or ambiguous, a line never becomes generated again.
      ['title', 'sub-title'].forEach((label, index) => {
        const expected = this.generatedHeaderLines[index];
        if (expected === null) return;
        const candidates = lines.filter(line => new RegExp(`^\\s*${label}\\s*:`, 'i').test(line));
        if (lines[index + 1] !== expected || candidates.length !== 1) this.generatedHeaderLines[index] = null;
      });
    },
    prefillNewTemplate() {
      if (!this.newTemplateDraft) return;
      this.reconcileGeneratedHeaders();
      if (!this.settings.destinationId || this.generatedHeaderLines.every(line => line === null)) return;
      try {
        const generated = newChunkTemplate(this.destination || {}).split('\n');
        const lines = this.settings.chunkTemplate.split('\n');
        this.generatedHeaderLines.forEach((expected, index) => {
          if (expected === null) return;
          lines[index + 1] = generated[index + 1];
          this.generatedHeaderLines[index] = generated[index + 1];
        });
        this.settings.chunkTemplate = lines.join('\n');
        this.templateHydrationError = '';
      }
      catch (error) { this.templateHydrationError = error.message; }
    },
    captureTemplateSelection(event) {
      const target = event.target;
      if (typeof target?.selectionStart !== 'number') return;
      this.templateSelection = { target, start: target.selectionStart, end: target.selectionEnd };
    },
    async insertTemplateField(field) {
      if (!this.templateFields.includes(field)) return;
      this.chunkView = 'edit';
      const text = this.settings.chunkTemplate;
      const selection = this.templateSelection;
      const start = Math.min(selection?.start ?? text.length, text.length);
      const end = Math.min(selection?.end ?? start, text.length);
      const token = `{{ ${field} }}`;
      this.settings.chunkTemplate = text.slice(0, start) + token + text.slice(end);
      this.markTemplateEdited();
      await this.$nextTick();
      const caret = start + token.length;
      const target = this.$refs.chunkTemplateInput?.$el?.querySelector('textarea') || selection?.target;
      target?.focus();
      target?.setSelectionRange(caret, caret);
      this.templateSelection = { target, start: caret, end: caret };
    },
    async clearChunkTemplate() {
      this.settings.chunkTemplate = '';
      this.markTemplateEdited();
      const target = this.$refs.chunkTemplateInput?.$el?.querySelector('textarea') || this.templateSelection?.target;
      await this.$nextTick();
      target?.focus();
      target?.setSelectionRange(0, 0);
      this.templateSelection = { target, start: 0, end: 0 };
    },
    setupRequest() {
      return {
        id: this.selectedConnection || 0,
        name: this.connectionName.trim(),
        connection: { ...this.connection },
        destinationId: this.settings.destinationId,
        topicField: this.settings.topicField,
        contentField: this.settings.contentField,
        chunkTemplate: this.settings.chunkTemplate,
        chunkTemplateIncludesHeader: true,
        publishedField: this.settings.publishedField,
        maximumChunks: Number(this.settings.maximumChunks),
      };
    },
    async saveSetup() {
      if (this.savingEntry || this.busy) return;
      this.clearFeedback();
      if (!this.connectionTested) { this.error = 'Test the current connection details before saving.'; return; }
      if (this.templateError) { this.error = this.templateError; return; }
      if (this.legacyConversionRequired && !this.legacyConversionAccepted) {
        this.error = 'Review Preview result and acknowledge that the editable format preserves line breaks before saving.';
        return;
      }
      if (!this.destination) { this.error = 'Select a valid document-version destination.'; return; }
      if (this.currentParent?.sourceId && this.settings.destinationId !== this.currentParent.destinationId) {
        this.error = 'The saved destination is immutable and cannot change.'; return;
      }
      const capError = this.maximumChunksRule(this.settings.maximumChunks);
      if (capError !== true) { this.error = capError; return; }
      if (!this.canSaveSetup) { this.error = 'Complete the connection, chunk template and chunk cap.'; return; }
      this.savingEntry = true;
      const request = this.setupRequest();
      try {
        if (this.previewOnly) {
          const source = this.currentParent;
          const schedule = source?.sourceId
            ? this.savedSchedules[source.sourceId] || { dailyEnabled: source.dailyEnabled, dailyTime: source.dailyTime }
            : { dailyEnabled: false, dailyTime: '08:00' };
          const saved = { ...request, isPending: false, id: source?.id || this.previewNextConnectionId++,
            ...schedule,
            sourceId: source?.sourceId || this.previewNextEntryId++,
            connection: { ...request.connection, clientSecret: '' },
            hasSecret: !!(this.hasSecret || request.connection.clientSecret) };
          if (source) Object.assign(source, saved);
          else this.sources.push(saved);
          this.savedSchedules[saved.sourceId] = { ...schedule };
          this.sources.forEach(card => {
            if (card.destinationId === saved.destinationId) card.maximumChunks = saved.maximumChunks;
          });
        } else {
          const result = await this.api('SaveSetup', request);
          if (!result?.id || !result?.sourceId) throw new Error('Save was not confirmed. Reload before retrying.');
          // Keep the returned identity if refreshing fails so retry cannot create a duplicate.
          this.selectedConnection = result.id;
          this.selectedSource = result.sourceId;
          this.hasSecret = true;
          try { await this.load(result.id); }
          catch (error) {
            this.connection.clientSecret = '';
            this.preview = null;
            throw new Error('Connection saved, but refresh failed. Reload connections before continuing. ' + (error.message || ''));
          }
        }
        this.connection.clientSecret = '';
        this.editor = '';
        this.notice = this.previewOnly ? 'Design preview: connection saved in memory. Nothing was imported.' : 'Connection saved. No chunks were imported.';
      } catch (error) { this.fail(error); }
      finally { this.savingEntry = false; }
    },
    async quickTest(id) {
      if (this.busy) return;
      this.clearFeedback();
      this.busy = true;
      const sequence = ++this.requestSequence;
      try {
        const result = this.previewOnly ? designPreviewArticles() : await this.api('TestSavedConnection', { id });
        if (sequence !== this.requestSequence) return;
        if (!Array.isArray(result?.records)) throw new Error('The connection did not return the expected news collection.');
        this.notice = `${this.previewOnly ? 'Design preview: ' : ''}Connection test succeeded. ${result.records.length} articles returned. No news was imported.`;
      } catch (error) { if (sequence === this.requestSequence) this.fail(error); }
      finally { if (sequence === this.requestSequence) this.busy = false; }
    },
    validDailyTime(value) { return /^([01]\d|2[0-3]):[0-5]\d$/.test(value || ''); },
    destinationLabel(id) {
      const target = this.destinations.find(x => x.id === id);
      return target
        ? `${this.categoryLabel(target.category)} › ${target.itemId} › ${target.version} — ${target.name}`
        : 'Destination unavailable';
    },
    async saveSchedule(source) {
      if (source.isPending || !source.sourceId || this.scheduleSaving) return;
      this.clearFeedback();
      if (!this.validDailyTime(source.dailyTime)) { this.error = 'Enter a valid daily time.'; return; }
      this.scheduleSaving = source.sourceId;
      try {
        if (!this.previewOnly) {
          await this.api('Schedule', { id: source.sourceId, dailyEnabled: !!source.dailyEnabled, dailyTime: source.dailyTime });
          const rows = await this.api('Connections');
          const saved = rows?.find(x => x.id === source.id);
          // Refresh only this card; retain other cards' unsaved schedule drafts.
          if (saved) {
            Object.assign(source, saved, { connection: { ...saved.connection, clientSecret: '' } });
            this.savedSchedules[saved.sourceId] = { dailyEnabled: saved.dailyEnabled, dailyTime: saved.dailyTime };
          }
        } else {
          this.savedSchedules[source.sourceId] = { dailyEnabled: source.dailyEnabled, dailyTime: source.dailyTime };
        }
        this.notice = `${this.previewOnly ? 'Design preview: schedule simulated' : 'Schedule saved'} for ${source.name}.`;
      } catch (error) { this.fail(error); }
      finally { this.scheduleSaving = null; }
    },
    showLogs(connectionId = 'all') {
      this.backToHistory();
      this.historyOpen = true;
      this.historyConnection = connectionId;
      this.historyPage = 1;
      this.historyStatus = 'All';
      this.historyConnections = this.sources.map(x => ({ id: x.id, name: x.name }));
      return this.refreshLogs();
    },
    backToHistory() {
      this.logResponseSequence += 1;
      this.viewedLog = null;
      this.logResponseLoading = false;
    },
    changeHistoryFilter() {
      this.historyPage = 1;
      this.backToHistory();
      return this.refreshLogs();
    },
    changeHistoryPage(page) {
      this.historyPage = page;
      this.backToHistory();
      return this.refreshLogs();
    },
    hideLogs() {
      this.historyOpen = false;
      this.backToHistory();
      this.logRequestSequence += 1;
      clearTimeout(this.logTimer);
      this.logs = [];
      this.loadingLogs = false;
    },
    async viewLogResponse(log) {
      this.viewedLog = { ...log };
      const selected = this.viewedLog;
      const sequence = ++this.logResponseSequence;
      const current = () => sequence === this.logResponseSequence && this.viewedLog === selected && this.historyOpen;
      this.logResponseLoading = true;
      this.logResponseError = '';
      this.logResponseText = '';
      this.logResponseMessage = '';
      this.logAttempts = [];
      this.responsePanel = undefined;
      try {
        const result = (this.previewOnly ? log : await this.api('LogResponse', { id: log.id })) || {};
        if (!current()) return;
        // Only the retry-aware contract guarantees classified safe error summaries.
        const safeContract = typeof result.legacy === 'boolean';
        Object.assign(selected, {
          status: result.status || log.status || 'Unknown',
          createdAt: result.createdAt || log.createdAt,
          attemptCount: result.attemptCount ?? null,
          maxAttempts: result.maxAttempts ?? null,
          nextAttemptAt: result.nextAttemptAt ?? null,
          legacy: result.legacy ?? !this.previewOnly,
          operation: result.operation || log.operation,
          connectionName: result.connectionName || log.connectionName,
          endpoint: result.endpoint || '',
          errorDetails: result.errorDetails || '',
          responseText: result.responseText || '',
          responseNote: result.responseNote || '',
        });
        this.logResponseMessage = ((safeContract || this.previewOnly) && result.message) || 'No safe summary was recorded for this run.';
        this.logAttempts = safeContract && Array.isArray(result.attempts) ? result.attempts.filter(x => x && typeof x === 'object') : [];
        if (typeof result.responseJson === 'string' && result.responseJson) {
          try { this.logResponseText = JSON.stringify(JSON.parse(result.responseJson), null, 2); }
          catch { this.logResponseText = result.responseJson; }
        }
      } catch (error) {
        if (current()) this.logResponseError = 'Could not load these details. Close and try again.';
      } finally {
        if (current()) this.logResponseLoading = false;
      }
    },
    logStatusColor(status) {
      return { Complete: 'success', Failed: 'error', Retrying: 'warning', Running: 'info', Queued: 'info', Cancelled: 'secondary' }[status] || 'secondary';
    },
    async runNow(connectionId = this.selectedConnection, entryId = this.selectedSource) {
      if (!entryId || !this.sources.some(x => x.id === connectionId && x.sourceId === entryId && !x.isPending)) return;
      this.logRequestSequence += 1;
      this.logs = [];
      this.loadingLogs = false;
      this.selectedConnection = connectionId;
      this.selectedSource = entryId;
      this.clearFeedback();
      this.runningEntry = entryId;
      try {
        if (this.previewOnly) {
          const entry = this.currentParent;
          const destination = this.destinations.find(x => x.id === entry?.destinationId);
          this.previewLogs.unshift({
            id: this.previewNextLogId++,
            entryId,
            createdAt: new Date().toISOString(),
            contentId: null,
            status: 'Complete',
            hasResponse: true,
            responseJson: JSON.stringify({ data: designPreviewArticles().records, total: designPreviewArticles().records.length }),
            oldChunks: destination?.chunks || 0,
            newChunks: destination?.chunks || 0,
            message: 'Design preview only — no import was queued and no chunks were written.',
            jobId: null,
          });
          this.notice = 'Run simulated in design preview. No job was queued.';
          await this.refreshLogs();
          return;
        }
        const result = await this.api('Run', { id: entryId });
        this.notice = `Sync queued (job ${result.jobId}). Refresh logs to follow its progress.`;
        await this.refreshLogs();
      } catch (error) {
        this.fail(error);
      } finally {
        this.runningEntry = null;
      }
    },
    async refreshLogs() {
      if (!this.historyOpen) return;
      const sequence = ++this.logRequestSequence;
      this.backToHistory();
      clearTimeout(this.logTimer);
      this.historyError = '';
      this.logs = [];
      this.loadingLogs = true;
      try {
        const request = {
          connectionId: typeof this.historyConnection === 'number' ? this.historyConnection : null,
          unsavedOnly: this.historyConnection === 'unsaved', status: this.historyStatus,
          page: this.historyPage, pageSize: 20,
        };
        let result;
        if (this.previewOnly) {
          const rows = this.previewLogs.map(log => {
            const source = this.sources.find(x => x.sourceId === log.entryId);
            return { ...log, connectionId: source?.id ?? null, connectionName: source?.name || 'Unsaved connection', unsaved: !source, operation: 'Sync run' };
          }).filter(x => (!request.connectionId || x.connectionId === request.connectionId)
            && (!request.unsavedOnly || x.unsaved) && (request.status === 'All' || x.status === request.status))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id);
          result = { items: rows.slice((request.page - 1) * 20, request.page * 20), total: rows.length, connections: this.historyConnections };
        } else result = await this.api('History', request);
        if (sequence !== this.logRequestSequence || !this.historyOpen) return;
        this.logs = result?.items || [];
        this.historyTotal = result?.total || 0;
        this.historyConnections = result?.connections || [];
        if (this.historyPage > this.historyPages) return this.changeHistoryPage(this.historyPages);
      } catch (error) {
        if (sequence === this.logRequestSequence && this.historyOpen) this.historyError = 'Could not load history. Refresh to try again.';
      } finally {
        if (sequence === this.logRequestSequence) this.loadingLogs = false;
      }
    },
    logTime(value) {
      if (!value) return '—';
      const text = String(value);
      const date = new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? text : text + 'Z');
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-GB', { timeZone: 'Asia/Bangkok' });
    },
    diagnosticText(value) {
      if (typeof value !== 'string') return JSON.stringify(value, null, 2);
      try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
    },
    async savePending() {
      if (this.deleteBusy) return;
      this.clearFeedback();
      if (!(await this.$refs.connectionForm.validate()).valid) return;
      for (const value of [this.connection.apiUrl, this.connection.authUrl]) {
        try {
          const url = new URL(value);
          const localMock = this.localDevelopment && url.protocol === 'http:'
            && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
            && url.pathname.startsWith('/api/mock/News/enterprise/v1/');
          if ((url.protocol !== 'https:' && !localMock) || url.username || url.password || url.hash) throw new Error();
        } catch {
          this.error = this.localDevelopment
            ? 'Use HTTPS URLs or the Development localhost News mock URLs, without embedded credentials or fragments.'
            : 'News API and Auth URLs must be valid HTTPS URLs without embedded credentials or fragments.';
          return;
        }
      }
      const request = { id: this.selectedConnection || 0, name: this.connectionName.trim(), connection: { ...this.connection } };
      this.savingEntry = true;
      try {
        if (this.previewOnly) {
          const source = this.currentParent;
          const saved = { ...source, ...request, id: source?.id || this.previewNextConnectionId++, sourceId: source?.sourceId || 0,
            connection: { ...request.connection, clientSecret: '' }, hasSecret: true, isPending: true, dailyEnabled: false };
          if (source) Object.assign(source, saved); else this.sources.push(saved);
        } else {
          const result = await this.api('SavePending', request);
          if (!result?.id || result.isPending !== true) throw new Error('Pending save was not confirmed. Reload before retrying.');
          this.selectedConnection = result.id;
          this.selectedSource = result.sourceId || 0;
          this.hasSecret = true;
          this.connection.clientSecret = '';
          try { await this.load(result.id); }
          catch { throw new Error('Saved as pending, but cards could not refresh. Reload connections before continuing.'); }
        }
        this.connection.clientSecret = '';
        this.preview = null;
        this.testedConnectionFingerprint = '';
        this.editor = '';
        this.notice = this.previewOnly ? 'Design preview: pending connection saved in memory.' : 'Saved as pending. Nothing was tested or imported; schedules are disabled.';
      } catch (error) { this.fail(error); }
      finally { this.savingEntry = false; }
    },
    formatBangkokDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Bangkok',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).formatToParts(date).map(part => [part.type, part.value]),
      );
      return `${parts.day}/${parts.month}/${parts.year}`;
    },
    required(value) {
      return !!String(value ?? '').trim() || 'Required';
    },
    secretRule(value) {
      return (this.selectedConnection && this.hasSecret) || !!String(value ?? '').trim() || 'Required for a new connection';
    },
    maximumChunksRule(value) {
      const maximumChunks = Number(value);
      if (!Number.isInteger(maximumChunks) || maximumChunks <= 0) return 'Enter a whole number greater than zero';
      if (maximumChunks > 100000) return 'Maximum total chunks cannot exceed 100,000';
      if (this.destination && maximumChunks < this.destination.chunks) {
        return `Maximum total chunks must be at least ${this.destination.chunks}`;
      }
      return true;
    },
    useMock() {
      if (this.previewOnly) {
        this.connectionName = this.connectionName || 'Sample news connection';
        this.connection = {
          apiUrl: 'https://preview.example/news-and-activities',
          authUrl: 'https://preview.example/auth/token',
          clientId: 'sample-client-id',
          clientSecret: 'sample-client-secret',
        };
        return;
      }
      const base = new URL(this.$call.urlApi || '/', window.location.origin);
      this.connection = {
        apiUrl: new URL('api/mock/News/enterprise/v1/news-and-activities', base).href,
        authUrl: new URL('api/mock/News/enterprise/v1/auth/token', base).href,
        clientId: 'local-client-id',
        clientSecret: 'local-client-secret',
      };
    },
    async testConnection() {
      if (this.busy) return;
      this.clearFeedback();
      this.preview = null;
      this.testedConnectionFingerprint = '';
      if (!this.selectedConnection && !this.connection.clientSecret?.trim()) {
        this.error = 'Client secret is required to test a new connection.';
        return;
      }
      if (!(await this.$refs.connectionForm.validate()).valid) return;

      if (this.previewOnly) {
        const fingerprint = this.connectionFingerprint;
        this.preview = designPreviewArticles();
        this.testedConnectionFingerprint = fingerprint;
        this.notice = 'Design preview: synthetic articles loaded. No network request was made.';
        return;
      }

      const fingerprint = this.connectionFingerprint;
      if (this.selectedConnection) {
        const sequence = ++this.requestSequence;
        this.busy = true;
        try {
          const preview = await this.api('TestSavedConnection', { id: this.selectedConnection, name: this.connectionName.trim(), connection: { ...this.connection } });
          if (sequence !== this.requestSequence) return;
          if (!Array.isArray(preview?.records)) {
            throw new Error('The saved connection did not return the expected news collection.');
          }
          this.preview = preview;
          this.testedConnectionFingerprint = fingerprint;
        } catch (error) {
          if (sequence === this.requestSequence) this.fail(error);
        } finally {
          if (sequence === this.requestSequence) this.busy = false;
        }
        return;
      }

      this.busy = true;
      this.preview = null;
      const sequence = ++this.requestSequence;
      try {
        const preview = await this.api('TestConnection', { ...this.connection, name: this.connectionName.trim() });
        if (sequence !== this.requestSequence) return;
        if (!Array.isArray(preview?.records)) throw new Error('The connection did not return the expected news collection.');
        this.preview = preview;
        this.testedConnectionFingerprint = fingerprint;
      } catch (error) { if (sequence === this.requestSequence) this.fail(error); }
      finally { if (sequence === this.requestSequence) this.busy = false; }
    },
  },
};
</script>

<style scoped>
.news-response-json {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 12px;
  padding: 16px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 8px;
}
.news-chunk-editor {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 7px;
  overflow: hidden;
  background: rgba(var(--v-theme-on-surface), 0.045);
}

.news-chunk-editor:focus-within {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.news-editable-chunk :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.55;
}

.news-editable-chunk {
  padding: 0 16px 12px;
}

.news-dialog-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.news-header,
.news-section-head,
.news-connection-head,
.news-entry-head,
.news-form-actions,
.news-entry-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.news-header {
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 16px 20px 12px;
}

.news-heading {
  flex: 1 1 280px;
}

.news-heading,
.news-connection-identity,
.news-editor-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.news-header-icon {
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.12);
}

.news-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
}

.news-subtitle {
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.4;
  white-space: normal;
  color: rgba(var(--v-theme-on-surface), 0.65);
}

.news-body {
  padding: 20px;
}

.news-min-width {
  min-width: 0;
}

.news-connection {
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
}

.news-connection-head {
  align-items: flex-start;
  padding: 16px;
  background: rgba(var(--v-theme-primary), 0.035);
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.news-parent-actions,
.news-icon-actions,
.news-schedule {
  display: flex;
  align-items: center;
  gap: 8px;
}

.news-parent-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.news-url,
.news-summary dd {
  overflow-wrap: anywhere;
}

.news-empty-entry {
  padding: 24px 16px;
  font-size: 0.85rem;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.65);
}

.news-card-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
}

.news-entry {
  padding: 14px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.018);
}

.news-entry--off {
  border-style: dashed;
}

.news-entry-head {
  align-items: flex-start;
}

.news-summary {
  display: grid;
  gap: 8px;
  margin: 14px 0 0;
}

.news-summary div {
  min-width: 0;
}

.news-summary dt {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.news-summary dd {
  margin: 2px 0 0;
  font-size: 0.8rem;
  line-height: 1.4;
}

.news-shared-note {
  margin-top: 10px;
  font-size: 0.75rem;
  color: rgb(var(--v-theme-primary));
}

.news-schedule {
  flex-wrap: wrap;
}

.news-schedule-time {
  flex: 0 1 180px;
  min-width: 150px;
}

.news-entry-actions {
  justify-content: flex-end;
  margin-top: 12px;
}

.news-logs,
.news-preview,
.news-editor-section {
  padding: 16px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.018);
}

.news-editor-heading {
  margin-bottom: 20px;
}

.news-form-actions {
  align-items: flex-start;
  flex-wrap: wrap;
}

.news-editor-section {
  margin-top: 16px;
}

.news-returned-fields {
  padding: 18px;
  border: 1px solid rgba(0, 137, 139, 0.28);
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.035);
}

.news-identity-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.news-preview-only-note {
  padding: 8px 10px;
  border-left: 2px solid rgb(var(--v-theme-primary));
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.72);
  background: rgba(var(--v-theme-surface), 0.72);
}

.news-mapping-card,
.news-chunk-preview-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.16);
  border-radius: 9px;
  background: rgb(var(--v-theme-surface));
}

.news-mapping-card {
  overflow: hidden;
}

.news-mapping-card-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 16px 16px 10px;
  font-size: 0.82rem;
}

.news-mapping-card-title span,
.news-mapping-label span {
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.news-mapping-row {
  display: grid;
  grid-template-columns: 125px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  padding: 8px 16px;
}

.news-mapping-row + .news-mapping-row {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.news-mapping-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 10px;
  border-left: 2px solid #48b6b0;
  font-size: 0.82rem;
}

.news-mapping-row :deep(.v-input) {
  min-width: 0;
}

.news-raw-json :deep(.v-expansion-panel) {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  background: rgb(var(--v-theme-surface));
}

.news-chunk-preview-card {
  padding: 16px;
}

.news-chunk-text {
  margin: 10px 0 0;
  padding: 16px;
  border-radius: 7px;
  background: rgba(var(--v-theme-on-surface), 0.045);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.55;
}

.news-cap-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 10px;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.news-json {
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.45;
}

@media (max-width: 800px) {
  .news-entry-grid {
    grid-template-columns: 1fr;
  }

  .news-section-head,
  .news-connection-head,
  .news-form-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .news-parent-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .news-identity-grid,
  .news-mapping-row {
    grid-template-columns: 1fr;
  }

  .news-mapping-row {
    gap: 8px;
    padding: 12px;
  }
}
</style>
